import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { X, CreditCard } from 'lucide-react';
import {
  getSubscriptionPackages, getDoctorPackage, assignDoctorPackage,
} from '../../redux/subscriptionSlice';
import { BILLING_PERIODS } from '../../data/accessControls';

const money = (p) => (p == null || String(p) === '' ? '' : `€${p}`);

// Admin-only: assign / change a doctor's subscription package (per-row on the
// Doctors list). Free is a no-charge comp but one-time (blocked once used). A paid
// plan needs a billing period and becomes a PENDING change — the doctor is asked
// to pay (crediting their current plan's unused value) on their next login.
const AssignPackageModal = ({ doctor, onClose }) => {
  const dispatch = useDispatch();
  const { packages, assigning } = useSelector((s) => s.subscription);
  const [selected, setSelected] = useState(''); // packageId, or '' = default (Free)
  const [period, setPeriod] = useState('');
  const [freeUsed, setFreeUsed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!packages.length) dispatch(getSubscriptionPackages());
      const res = await dispatch(getDoctorPackage(doctor.doctorId));
      if (active) {
        setSelected(res.payload?.packageId || '');
        setFreeUsed(!!res.payload?.freeUsed);
        setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor.doctorId]);

  const defaultPkg = packages.find((p) => p.isDefault);
  const selectedPkg = packages.find((p) => p.id === selected) || null;
  const isPaid = !!selectedPkg && !selectedPkg.isDefault;

  // Enabled + priced periods for the selected paid plan.
  const bp = selectedPkg?.billingPeriods || {};
  const periods = BILLING_PERIODS
    .filter((x) => bp[x.id] && bp[x.id].enabled && String(bp[x.id].price ?? '').trim() !== '')
    .map((x) => ({ ...x, price: bp[x.id].price }));
  const needsPeriod = isPaid && periods.length > 0;

  // Default the period to the first option when the selected plan changes.
  useEffect(() => {
    if (needsPeriod) {
      setPeriod((prev) => (periods.some((p) => p.id === prev) ? prev : periods[0].id));
    } else {
      setPeriod('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const canSave = !loading && !assigning && !(needsPeriod && !period);

  const save = async () => {
    try {
      const res = await dispatch(assignDoctorPackage({
        doctorId: doctor.doctorId,
        packageId: selected || null,
        period: needsPeriod ? period : undefined,
      })).unwrap();
      toast.success(res?.pending
        ? 'Plan set — the doctor will be asked to pay on their next login.'
        : 'Subscription package updated');
      onClose();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Could not update package — is the backend deployed?');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="assign-package-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[92vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#4ABDBD]" />
            <h2 className="text-lg font-bold text-[#1a365d]">Subscription package</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-slate-500">
            Assign a package to <span className="font-semibold text-gray-700">{doctor.name || doctor.doctorId}</span>.
          </p>

          {loading ? (
            <div className="py-6 flex justify-center">
              <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Package</label>
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  data-testid="assign-package-select"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                  <option value="" disabled={freeUsed}>
                    Default ({defaultPkg ? defaultPkg.name : 'Free'}){freeUsed ? ' — already used' : ''}
                  </option>
                  {packages
                    .filter((p) => !p.isDefault && p.purchasable !== false)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                {freeUsed && (
                  <p className="text-[11px] text-amber-600 mt-1.5">This doctor has already used their free plan — choose a paid plan.</p>
                )}
              </div>

              {/* Billing period — required for a paid plan with periods */}
              {needsPeriod && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Billing period</label>
                  <div className="grid grid-cols-2 gap-2">
                    {periods.map((p) => {
                      const on = p.id === period;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPeriod(p.id)}
                          data-testid={`assign-period-${p.id}`}
                          className={`text-left rounded-xl border px-3 py-2 transition-all ${on ? 'border-[#4ABDBD] ring-1 ring-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <div className="text-sm font-semibold text-gray-700">{p.label}</div>
                          <div className="text-xs text-slate-500">{money(p.price)} · {p.days} days</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isPaid ? (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <CreditCard className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>The doctor will be <strong>asked to pay</strong> for this plan on their next login. Any unused value from their current plan is credited.</span>
                </div>
              ) : (
                <p className="text-xs text-slate-400">
                  “Default” puts the doctor on the free plan (no charge). Selecting a paid plan asks the doctor to pay.
                </p>
              )}
            </>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            data-testid="assign-package-save"
            className={`px-4 py-2 rounded-xl text-white font-semibold text-sm transition-all ${
              !canSave ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg hover:scale-105'
            }`}
          >
            {assigning ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPackageModal;
